import React from "react";

import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Skeleton,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";

import {
  PIPELINE_RUN_LIMIT_OPTIONS,
  type PipelineRunRow,
  getIntentLabel,
} from "src/domains/application/hooks/usePipelineRunsApi";

import { PIPELINE_MODE_SHORT } from "./dag-types";
import {
  formatRelativeTime,
  formatDuration,
  shortSha,
} from "./shared/pipeline-helpers";

// ── Types ────────────────────────────────────────────────────────────────────

export interface RunsListProps {
  pipelineMode: string;
  runs: PipelineRunRow[];
  total: number;
  limit: number;
  offset: number;
  hasNext: boolean;
  hasPrevious: boolean;
  loading: boolean;
  error: string | null;
  onLimitChange: (limit: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onRetry: () => void;
  selectedRunId: string | null;
  onSelectRun: (sagaId: string | null) => void;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// ── Status icon (matching mockup .rusha-pl-sicon) ────────────────────────────

function RunStatusIcon({
  status,
  size = "sm",
}: {
  status: string;
  size?: "sm" | "lg";
}): React.ReactElement {
  const dim = size === "lg" ? 26 : 18;
  const fontSize = size === "lg" ? 14 : 11;

  if (status === "completed") {
    return (
      <Box
        sx={{
          width: dim,
          height: dim,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "#10b981",
          bgcolor: "rgba(16,185,129,0.12)",
          fontSize,
          fontWeight: 700,
        }}
      >
        ✓
      </Box>
    );
  }
  if (status === "failed") {
    return (
      <Box
        sx={{
          width: dim,
          height: dim,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "#f43f5e",
          bgcolor: "rgba(244,63,94,0.12)",
          fontSize,
          fontWeight: 700,
        }}
      >
        ✕
      </Box>
    );
  }
  if (status === "in_progress") {
    return (
      <Box
        sx={{
          width: size === "lg" ? 22 : 14,
          height: size === "lg" ? 22 : 14,
          borderRadius: "50%",
          border: `2px solid rgba(59,130,246,0.25)`,
          borderTopColor: "#3b82f6",
          animation: `${spin} 0.8s linear infinite`,
          flexShrink: 0,
        }}
      />
    );
  }
  // queued / pending
  return (
    <Box
      sx={{
        width: dim,
        height: dim,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "#94a3b8",
        bgcolor: "rgba(148,163,184,0.12)",
        fontSize,
        fontWeight: 700,
      }}
    >
      ○
    </Box>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function computeDurationSec(run: PipelineRunRow): number | null {
  if (run.status === "in_progress" || run.status === "queued") return null;
  if (!run.completedAt) return null;
  const start = new Date(run.createdAt).getTime();
  const end = new Date(run.completedAt).getTime();
  return Math.round((end - start) / 1000);
}

// ── Component ──────────────────────────────────────────────────────────────────

export function PipelineRunsList({
  pipelineMode,
  runs,
  total,
  limit,
  offset,
  hasNext,
  hasPrevious,
  loading,
  error,
  onLimitChange,
  onNextPage,
  onPreviousPage,
  onRetry,
  selectedRunId,
  onSelectRun,
}: RunsListProps): React.ReactElement {
  const theme = useTheme();

  if (loading) {
    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "12px",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        {/* Rail header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "text.secondary",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          Pipeline runs
        </Box>
        {[1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              gap: 1.25,
              alignItems: "center",
              borderBottom:
                i < 3 ? `1px solid ${theme.palette.divider}` : "none",
            }}
          >
            <Skeleton variant="circular" width={18} height={18} />
            <Box sx={{ flex: 1 }}>
              <Skeleton
                variant="text"
                width="80%"
                height={14}
                sx={{ mb: 0.5 }}
              />
              <Skeleton variant="text" width="60%" height={12} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  const page = Math.floor(offset / limit) + 1;

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "12px",
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      {/* Rail header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          fontSize: 11,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "text.secondary",
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <span>Pipeline runs</span>
          <FormControl size="small" sx={{ minWidth: 72 }}>
            <Select
              value={limit}
              onChange={(event) => onLimitChange(Number(event.target.value))}
              inputProps={{ "aria-label": "Runs per page" }}
              sx={{ fontSize: 11, height: 28 }}
            >
              {PIPELINE_RUN_LIMIT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: "error.main", mb: 1 }}>
            {error}
          </Typography>
          <Button size="small" onClick={onRetry}>
            Try again
          </Button>
        </Box>
      ) : runs.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            No pipeline runs yet.
          </Typography>
        </Box>
      ) : (
        runs.map((run, index) => {
          const isSelected = run.sagaId === selectedRunId;
          const durationSec = computeDurationSec(run);
          const modeLabel = PIPELINE_MODE_SHORT[pipelineMode] ?? pipelineMode;

          return (
            <Box
              key={run.sagaId}
              onClick={() => onSelectRun(run.sagaId)}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
                px: 2,
                py: 1.5,
                cursor: "pointer",
                borderBottom:
                  index < runs.length - 1
                    ? `1px solid ${theme.palette.divider}`
                    : "none",
                borderLeft: `2px solid ${
                  isSelected ? theme.palette.primary.main : "transparent"
                }`,
                "&:hover": { bgcolor: alpha("#3b82f6", 0.05) },
                ...(isSelected && { bgcolor: alpha("#3b82f6", 0.08) }),
              }}
            >
              <RunStatusIcon status={run.status} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: "text.primary",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {run.commitMessage ?? getIntentLabel(run.intent)}
                </Typography>
                <Box
                  sx={{
                    fontSize: 11,
                    color: "text.tertiary" as const,
                    mt: 0.375,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexWrap: "wrap",
                  }}
                >
                  {run.branch && <span>{run.branch}</span>}
                  {shortSha(run.sha) && (
                    <Box
                      component="code"
                      sx={{ fontFamily: "monospace", fontSize: "10.5px" }}
                    >
                      {shortSha(run.sha)}
                    </Box>
                  )}
                  {modeLabel && <span>· {modeLabel}</span>}
                  <span>· attempt {run.attempt ?? 1}</span>
                  <span>· {formatRelativeTime(run.createdAt)}</span>
                  {run.failedAt && (
                    <Typography
                      component="span"
                      sx={{ color: "#f43f5e", fontSize: 11 }}
                    >
                      · failed at {run.failedAt}
                    </Typography>
                  )}
                  {durationSec !== null && (
                    <span>· {formatDuration(durationSec)}</span>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })
      )}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography sx={{ fontSize: 11, color: "text.secondary" }}>
          {total === 0 ? "0 runs" : `Page ${page} · ${total} total`}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Button
            size="small"
            onClick={onPreviousPage}
            disabled={!hasPrevious || loading}
            sx={{ minWidth: 0, px: 1, fontSize: 11, textTransform: "none" }}
          >
            Previous
          </Button>
          <Button
            size="small"
            onClick={onNextPage}
            disabled={!hasNext || loading}
            sx={{ minWidth: 0, px: 1, fontSize: 11, textTransform: "none" }}
          >
            Next
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default PipelineRunsList;
